import { AI_CHAT_API_URL, API_URL } from "../Config/index.js";
import { getAuthTokenPayload } from "../Utils/index.js";

// Helper to get auth token from localStorage
const getAuthToken = () => localStorage.getItem("authToken");

/**
 * AI Agent Chat - calls the central AI agent endpoint.
 * @param {string} prompt - The user's message
 * @param {string|null} openAiConversationId - Conversation ID for continuity (null for new chat)
 * @returns {Promise<{ response: string, openAiConversationId: string }>}
 */
export const AIAgentChat = async (prompt, openAiConversationId = null) => {
  try {
    const tokenPayload = getAuthTokenPayload();
    const clientId = tokenPayload?.clientId;
    const userId = tokenPayload?.userId;

    if (!clientId || !userId) {
      throw new Error("User not authenticated. Please log in again.");
    }

    const token = getAuthToken();
    const requestPayload = {
      clientId,
      userId,
      prompt,
      openAiConversationId
    };

    console.log("AIAgentChat - Request payload:", requestPayload);

    const response = await fetch(`${API_URL}/ai-agent/r1/r2`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(token ? { Authorization: token } : {})
      },
      body: JSON.stringify(requestPayload)
    });

    console.log("AIAgentChat - Response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("AIAgentChat - API Error:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("AIAgentChat - Raw response:", data);

    if (!data.status) {
      throw new Error(data.uiMsg || data.message || "AI agent returned an error.");
    }

    if (!data.response) {
      throw new Error("Empty response received from AI agent.");
    }

    return {
      response: data.response,
      openAiConversationId: data.openAiConversationId || null
    };

  } catch (error) {
    console.error("AIAgentChat - Error:", error);

    let errorMessage = "Sorry, I encountered an error. Please try again.";
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      errorMessage = "Unable to connect to AI service. Please check your internet connection.";
    } else if (error.message.includes("500")) {
      errorMessage = "AI service is temporarily unavailable. Please try again in a moment.";
    } else if (error.message.includes("429")) {
      errorMessage = "Too many requests. Please wait a moment before trying again.";
    } else if (error.message.includes("404")) {
      errorMessage = "AI service endpoint not found. Please contact support.";
    } else if (error.message.includes("401") || error.message.includes("authenticated")) {
      errorMessage = "Session expired. Please log in again.";
    } else if (error.message.includes("400")) {
      errorMessage = "Invalid request. Please try rephrasing your message.";
    } else if (error.message.includes("403")) {
      errorMessage = "Access denied. Please contact support.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

export const AIChat = async (userMessage, conversationHistory = []) => {
  try {
    console.log('AIChat Service - Calling API with message:', userMessage);
    
    // Prepare the request payload
    const requestPayload = {
      chatInput: userMessage,
      context: 'social_media_content_creation',
      timestamp: new Date().toISOString(),
  platform: 'Oruclicks',
      userId: 'web_user_' + Date.now(),
      conversationHistory: conversationHistory.slice(-6).map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.timestamp.toISOString()
      })),
      requestType: 'content_generation',
      language: 'en',
      tone: 'professional_friendly',
      sessionId: 'session_' + Date.now(),
  userAgent: 'Oruclicks-WebApp'
    };

    console.log('AIChat Service - Request payload:', requestPayload);
    
    const response = await fetch(AI_CHAT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
  'User-Agent': 'Oruclicks-WebApp'
      },
      body: JSON.stringify(requestPayload)
    });

    console.log('AIChat Service - Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('AIChat Service - API Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('AIChat Service - Raw API Response:', data);
    
    // Enhanced response parsing to handle your specific format
    let aiContent = '';
    
    if (data.text) {
      // Your API returns: {"text": "{\"content\":null}"}
      let textContent = data.text;
      
      try {
        // Try to parse the text content as JSON
        const parsedText = JSON.parse(textContent);
        console.log('AIChat Service - Parsed text content:', parsedText);
        
        // Extract the first key (which is the actual content)
        const keys = Object.keys(parsedText);
        if (keys.length > 0) {
          aiContent = keys[0]; // The actual content is the key
        } else {
          aiContent = textContent;
        }
      } catch (parseError) {
        // If parsing fails, use the text as is
        console.log('AIChat Service - Could not parse text as JSON, using as is');
        aiContent = textContent;
      }
    } else if (typeof data === 'string') {
      aiContent = data;
    } else if (data.response) {
      aiContent = data.response;
    } else if (data.message) {
      aiContent = data.message;
    } else if (data.content) {
      aiContent = data.content;
    } else if (data.result) {
      aiContent = data.result;
    } else if (data.output) {
      aiContent = data.output;
    } else if (data.chatOutput) {
      aiContent = data.chatOutput;
    } else if (data.aiResponse) {
      aiContent = data.aiResponse;
    } else if (data.data) {
      if (typeof data.data === 'string') {
        aiContent = data.data;
      } else if (data.data.response) {
        aiContent = data.data.response;
      } else if (data.data.message) {
        aiContent = data.data.message;
      } else if (data.data.chatOutput) {
        aiContent = data.data.chatOutput;
      } else {
        aiContent = JSON.stringify(data.data);
      }
    } else if (data.choices && data.choices[0] && data.choices[0].message) {
      aiContent = data.choices[0].message.content;
    } else {
      const stringValues = Object.values(data).filter(value => typeof value === 'string' && value.length > 10);
      if (stringValues.length > 0) {
        aiContent = stringValues[0];
      } else {
        aiContent = 'I received your message but the response format was unexpected. Please try rephrasing your request.';
        console.warn('AIChat Service - Unexpected response format:', data);
      }
    }

    // Clean up the content - remove extra quotes and braces
    aiContent = aiContent.trim();
    
    // Remove wrapping quotes if present
    if (aiContent.startsWith('"') && aiContent.endsWith('"')) {
      aiContent = aiContent.slice(1, -1);
    }
    
    // Remove wrapping braces if present
    if (aiContent.startsWith('{') && aiContent.endsWith('}')) {
      try {
        // Try to parse as JSON to extract the key
        const parsed = JSON.parse(aiContent);
        const keys = Object.keys(parsed);
        if (keys.length > 0) {
          aiContent = keys[0];
        }
      } catch {
        // If parsing fails, remove braces manually
        aiContent = aiContent.slice(1, -1);
      }
    }
    
    // Remove any remaining escape characters
    aiContent = aiContent.replace(/\\"/g, '"');
    
    if (!aiContent) {
      throw new Error('Empty response received from AI service');
    }

    console.log('AIChat Service - Final processed content:', aiContent);
    return aiContent;

  } catch (error) {
    console.error('AIChat Service - Error:', error);
    
    let errorMessage = 'Sorry, I encountered an error. Please try again.';
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage = 'Unable to connect to AI service. Please check your internet connection and try again.';
    } else if (error.message.includes('500')) {
      errorMessage = 'AI service is temporarily unavailable. Please try again in a moment.';
    } else if (error.message.includes('429')) {
      errorMessage = 'Too many requests. Please wait a moment before trying again.';
    } else if (error.message.includes('404')) {
      errorMessage = 'AI service endpoint not found. Please contact support if this persists.';
    } else if (error.message.includes('400')) {
      errorMessage = 'Invalid request format. Please try rephrasing your message.';
    } else if (error.message.includes('403')) {
      errorMessage = 'Access denied to AI service. Please contact support.';
    } else if (error.message.includes('timeout')) {
      errorMessage = 'Request timed out. Please try again with a shorter message.';
    } else if (error.message.includes('Empty response')) {
      errorMessage = 'AI service returned an empty response. Please try rephrasing your request.';
    }

    throw new Error(errorMessage);
  }
};

/**
 * Fetch AI chat conversation history for a given month.
 * @param {string} monthYear - e.g. "Mar-2026"
 * @returns {Promise<Array>} - Array of conversation objects from the API
 */
export const getAIChatHistory = async (monthYear) => {
  try {
    const tokenPayload = getAuthTokenPayload();
    const userId = tokenPayload?.userId;

    if (!userId) {
      throw new Error("User not authenticated. Please log in again.");
    }

    const token = getAuthToken();
    const response = await fetch(`${API_URL}/ai-agent/${userId}/${monthYear}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(token ? { Authorization: token } : {})
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("getAIChatHistory - API Error:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("getAIChatHistory - Response:", data);

    if (!data.status) {
      throw new Error(data.uiMsg || data.message || "Failed to fetch history.");
    }

    return data.data || [];
  } catch (error) {
    console.error("getAIChatHistory - Error:", error);
    throw error;
  }
};
